import { useMapEvent, useMap } from 'react-leaflet';
import { useLayoutEffect, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import L from 'leaflet';

const mapRange = 360;

const latLngBoundsToBox = bounds => ({
  north: bounds.getNorth(),
  south: bounds.getSouth(),
  east: bounds.getNorthEast().wrap().lng,
  west: bounds.getSouthWest().wrap().lng,
});

const checkNumber = value => value != null && !Number.isNaN(value);

const GeomanControl = ({
  onChange, north, east, south, west,
}) => {
  const [currentLayer, setCurrentLayer] = useState(null);
  const map = useMap();

  useLayoutEffect(() => {
    map.pm.addControls({
      position: 'topleft',
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawPolygon: false,
      drawCircle: false,
      editMode: true,
      dragMode: true,
      cutPolygon: false,
      oneBlock: true,
      removalMode: true,
    });

    map.pm.Toolbar.changeActionsOfControl('removalMode', []);
    map.pm.Toolbar.changeActionsOfControl('dragMode', []);
    map.pm.Toolbar.changeActionsOfControl('editMode', ['cancel']);

    const buttons = map.pm.Toolbar.getButtons();
    // eslint-disable-next-line no-underscore-dangle
    buttons.removalMode._button.title = 'Remove Selection';
    return () => {
      map.pm.removeControls();
    };
  });

  useEffect(() => {
    if (checkNumber(north) && checkNumber(south) && checkNumber(east) && checkNumber(west)) {
      if (currentLayer) {
        map.removeLayer(currentLayer);
      }
      let westLong = west;
      if (west > east) {
        westLong = west - mapRange;
      }
      const southWest = L.latLng(south, westLong);
      const northEast = L.latLng(north, east);
      const bounds = L.latLngBounds(southWest, northEast);
      const layer = L.rectangle(bounds);
      map.addLayer(layer);
      setCurrentLayer(layer);
      map.setView(bounds.getCenter());
    } else if (currentLayer) {
      map.removeLayer(currentLayer);
      setCurrentLayer(null);
    }
  }, [north, south, east, west]);

  useMapEvent('pm:create', (e) => {
    const { layer } = e;
    if (currentLayer) {
      map.removeLayer(currentLayer);
    }
    map.removeLayer(layer);
    setCurrentLayer(layer);
    onChange(latLngBoundsToBox(layer.getBounds()));
  });

  useMapEvent('pm:globaleditmodetoggled', () => {
    if (map.pm.globalEditModeEnabled()) {
      currentLayer.on('pm:markerdragend', () => {
        onChange(latLngBoundsToBox(currentLayer.getBounds()));
        map.pm.disableGlobalEditMode();
      });
    }
  });

  useMapEvent('pm:globaldragmodetoggled', () => {
    if (map.pm.globalDragModeEnabled()) {
      currentLayer.on('pm:dragend', () => {
        onChange(latLngBoundsToBox(currentLayer.getBounds()));
        map.pm.disableGlobalDragMode();
      });
    }
  });

  useMapEvent('pm:globalremovalmodetoggled', () => {
    if (map.pm.globalRemovalModeEnabled()) {
      if (currentLayer) {
        map.removeLayer(currentLayer);
        setCurrentLayer(null);
      }
      onChange(null);
      map.pm.disableGlobalRemovalMode();
    }
  });

  return null;
};

GeomanControl.propTypes = {
  onChange: PropTypes.func.isRequired,
  north: PropTypes.number,
  south: PropTypes.number,
  east: PropTypes.number,
  west: PropTypes.number,
};

GeomanControl.defaultProps = {
  north: null,
  south: null,
  east: null,
  west: null,
};

export default GeomanControl;
